import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  InteractiveMaterialFlashcards,
  InteractiveMaterialResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { Text } from "@core/index";
import {
  VideoSection,
  ContentSection,
  EditButtons,
  SaveCancelButtons,
  useEditState,
  useVideoSection,
  useTimeInput,
  convertSecondsToTime,
} from "./utils";

interface FlashCardTabProps {
  material: InteractiveMaterialResponseDto;
  onFlashcardsChange?: (flashcards: InteractiveMaterialFlashcards[]) => void;
}

export const FlashCardTab = ({
  material,
  onFlashcardsChange,
}: FlashCardTabProps): JSX.Element => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [flashcards, setFlashcards] = useState<InteractiveMaterialFlashcards[]>(
    material.generatedFlashcards || [],
  );

  const videoSection = useVideoSection();
  const timeInput = useTimeInput({
    onTimeChange: (seconds) => {
      if (editingItem) {
        setEditingItem({
          ...editingItem,
          startTime: seconds,
        });
      }
    },
  });

  const {
    editingIndex,
    editingItem,
    setEditingItem,
    handleEdit: baseHandleEdit,
    handleDelete,
    handleSave: baseHandleSave,
    handleCancel: baseHandleCancel,
    handleCreate: baseHandleCreate,
  } = useEditState({
    items: flashcards,
    onItemsChange: onFlashcardsChange,
    createNewItem: () => ({
      front: "",
      back: "",
      startTime: 0,
    }),
    setItems: setFlashcards,
    containerRef,
  });

  const handleEdit = (index: number) => {
    baseHandleEdit(index);
    const card = flashcards[index];
    timeInput.setTimeInputValue(
      card.startTime ? convertSecondsToTime(card.startTime) : "00:00",
    );
  };

  const handleSave = (index: number) => {
    baseHandleSave(index);
    timeInput.setTimeInputValue("");
  };

  const handleCancel = () => {
    baseHandleCancel();
    timeInput.setTimeInputValue("");
  };

  const handleCreate = () => {
    baseHandleCreate();
    timeInput.setTimeInputValue("00:00");
  };

  useEffect(() => {
    if (containerRef.current && editingIndex !== null) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [editingIndex]);

  return (
    <div className="h-[calc(100vh-20rem)] max-h-[800px] min-h-[600px] flex flex-col lg:flex-row gap-4 relative">
      <VideoSection
        {...videoSection}
        youtubeUrl={material.youtubeUrl}
        generatorMode={material.generatorMode}
      />

      <ContentSection
        title={t("interactive_material.flashcards.title")}
        onAddNew={handleCreate}
        addButtonLabel={t("interactive_material.flashcards.addNew")}
        isEmpty={flashcards.length === 0}
        emptyStateMessage={t("interactive_material.flashcards.empty")}
        containerRef={containerRef}
      >
        {flashcards.map((card, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-all hover:shadow-md"
          >
            {editingIndex === index && editingItem ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.flashcards.front")}
                  </label>
                  <input
                    type="text"
                    value={editingItem.front}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        front: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
                    placeholder={t(
                      "interactive_material.flashcards.frontPlaceholder",
                    )}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.flashcards.back")}
                  </label>
                  <textarea
                    value={editingItem.back}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        back: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors resize-none"
                    rows={3}
                    placeholder={t(
                      "interactive_material.flashcards.backPlaceholder",
                    )}
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <SaveCancelButtons
                    onSave={() => handleSave(index)}
                    onCancel={handleCancel}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="space-y-2 flex-1 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Text variant={"h3"} className="break-all">
                      {card.front}
                    </Text>
                  </div>
                  <Text
                    variant="p"
                    status="subtle"
                    size={"base"}
                    className="break-words"
                  >
                    {card.back}
                  </Text>
                </div>
                <EditButtons
                  onEdit={() => handleEdit(index)}
                  onDelete={() => handleDelete(index)}
                />
              </div>
            )}
          </div>
        ))}
      </ContentSection>
    </div>
  );
};

export default FlashCardTab;
